import React from 'react'
import SbEditable from 'storyblok-react'
import { PillarItem, type PillarItemProps } from './PillarItem'
import RichText from 'src/utils/RichText'
import resolveLink from '@/utils/resolveLink'
import { Link } from 'gatsby'

export interface ThreePillarsProps {
  blok: StoryblokComponent & {
    first_pillar_icon: string
    first_pillar_title: string
    first_pillar_link: string
    first_pillar_items: PillarItemProps[]

    second_pillar_icon: string
    second_pillar_title: string
    second_pillar_link: string
    second_pillar_items: PillarItemProps[]

    third_pillar_icon: string
    third_pillar_title: string
    third_pillar_link: string
    third_pillar_items: PillarItemProps[]
    
    marginTop?: number
    marginBottom?: number
  }
}

export const ThreePillars = ({ blok }: ThreePillarsProps) => {
  const marginTopClass = blok.marginTop ? `${blok.marginTop}px` : ''
  const marginBottomClass = blok.marginBottom ? `${blok.marginBottom}px` : ''

  // @INFO: Define pillar data for easier mapping
  const pillars = [
    {
      icon: blok.first_pillar_icon,
      title: blok.first_pillar_title,
      items: blok.first_pillar_items,
      bgColor: 'bg-teal-1000/30',
      itemVariant: 'primary' as const,
      link: blok.first_pillar_link
    },
    {
      icon: blok.second_pillar_icon,
      title: blok.second_pillar_title,
      items: blok.second_pillar_items,
      bgColor: 'bg-[#00577E]/40', // @INFO: Slate/gray background for second pillar
      itemVariant: 'secondary' as const,
      link: blok.second_pillar_link
    },
    {
      icon: blok.third_pillar_icon,
      title: blok.third_pillar_title,
      items: blok.third_pillar_items,
      bgColor: 'bg-brightgreen/20', // @INFO: Light green background for third pillar
      itemVariant: 'tertiary' as const,
      link: blok.third_pillar_link
    }
  ]

  return (
    <div
      className="flex justify-center font-display"
      style={{
        marginTop: marginTopClass,
        marginBottom: marginBottomClass,
      }}
    >
      {/** @ts-ignore: Storyblok typing issue with children **/}
      <SbEditable content={blok} key={blok._uid}>
        <div className="max-w-7xl flex flex-col md:flex-row justify-between gap-14">
          {pillars.map((pillar, index) => (
            <div 
              key={index} 
              className={`w-80 text-white rounded-lg py-6 px-10 ${pillar.bgColor} flex flex-col items-center justify-start`}
            >
              <div className="p-6 flex flex-col items-center">
                <div className="w-20 h-28 mb-3">
                  <Link to={resolveLink(pillar.link)}>
                    <img src={pillar.icon} alt={`${index + 1} pillar icon`} className="w-full h-full" />
                  </Link>

                </div>
                <div className="uppercase font-medium text-center text-sm mt-4">
                  <RichText>{pillar.title}</RichText>
                </div>
              </div>
              
              <div className="flex flex-col justify-center gap-4 md:gap-6">
                {pillar.items.map((item, idx) => (
                  <PillarItem 
                    key={idx} 
                    {...item} 
                    variant={pillar.itemVariant} 
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SbEditable>
    </div>
  )
}

ThreePillars.displayName = 'Three Pillars'